import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { ShieldAlert, RefreshCw, Loader2, UserX, Fingerprint, Globe } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString("es-MX", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
  catch { return iso || "—"; }
};
const truncFp = (fp) => (!fp ? "—" : fp.length > 14 ? `${fp.slice(0, 6)}…${fp.slice(-4)}` : fp);

const SOURCE_LABEL = { ban_gate: "Dispositivo/IP bloqueado", blocked_login: "Login de usuario bloqueado" };

/* Panel: intentos de acceso de personas bloqueadas (IP, dispositivo, hora y cliente identificado). */
export const AdminBlockedAttemptsPanel = () => {
  const { authHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/blocked-attempts?days=${days}&limit=300`, { headers: authHeader() });
      setData(r.data);
    } catch { setData({ entries: [], count: 0 }); }
    finally { setLoading(false); }
  }, [authHeader, days]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="glass-dark rounded-2xl p-4 space-y-3" data-testid="blocked-attempts-panel">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h3 className="font-serif text-lg text-white flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-red-400" /> Intentos de acceso bloqueados</h3>
          <p className="text-[11px] text-neutral-500">Cada vez que una persona bloqueada (o su dispositivo/IP) intenta entrar, registrarse o publicar, queda aquí con fecha, hora, IP y el cliente al que corresponde.</p>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="blocked-attempts-period" value={days} onChange={(e) => setDays(Number(e.target.value))} className="bg-black/40 border border-white/10 rounded px-2 py-1.5 text-xs text-neutral-200">
            <option value={7}>Últimos 7 días</option>
            <option value={30}>Últimos 30 días</option>
            <option value={90}>Últimos 90 días</option>
          </select>
          <button data-testid="blocked-attempts-refresh" onClick={load} className="h-8 w-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><RefreshCw className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      {loading ? (
        <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-[#d4af37] mx-auto" /></div>
      ) : !data?.entries?.length ? (
        <p className="text-xs text-neutral-500 italic text-center py-6" data-testid="blocked-attempts-empty">Sin intentos bloqueados en este periodo.</p>
      ) : (
        <div className="space-y-2">
          {data.entries.map((e) => (
            <div key={e.id} data-testid={`blocked-attempt-${e.id}`} className="rounded-xl border border-red-500/20 bg-red-500/5 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2 text-[11px]">
                <span className="text-neutral-300 font-mono">{fmt(e.at)}{e.count > 1 && <span className="ml-2 text-red-300">×{e.count}</span>}</span>
                <span className="px-2 py-0.5 rounded-full border border-red-400/40 text-red-200 uppercase tracking-widest text-[9px]">{SOURCE_LABEL[e.source] || e.source}</span>
              </div>
              <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px]">
                <div className="flex items-center gap-1.5 text-white"><Globe className="w-3.5 h-3.5 text-[#d4af37]" /> IP <span className="font-mono" data-testid="blocked-attempt-ip">{e.ip || "—"}</span></div>
                <div className="flex items-center gap-1.5 text-neutral-300"><Fingerprint className="w-3.5 h-3.5 text-[#d4af37]" /> Dispositivo <span className="font-mono" title={e.fingerprint}>{truncFp(e.fingerprint)}</span></div>
                <div className="text-neutral-400 truncate" title={e.path}>Ruta: <span className="font-mono">{e.method} {e.path}</span></div>
              </div>
              {e.identifier && <div className="mt-1 text-[11px] text-neutral-400">Intentó entrar como: <span className="text-white">{e.identifier}</span></div>}
              {e.blocked_user ? (
                <div className="mt-2 rounded-lg bg-black/40 border border-white/10 p-2 text-[11px]" data-testid="blocked-attempt-user">
                  <div className="flex items-center gap-1.5 text-red-200 font-semibold"><UserX className="w-3.5 h-3.5" /> Cliente bloqueado identificado</div>
                  <div className="text-neutral-200 mt-0.5">{e.blocked_user.name} · @{e.blocked_user.username} · ID {e.blocked_user.visible_id || "—"}</div>
                  <div className="text-neutral-400">{e.blocked_user.email} · {e.blocked_user.phone || "sin teléfono"} · {e.blocked_user.country || "—"}</div>
                </div>
              ) : (
                <div className="mt-2 text-[11px] text-neutral-500 italic">Bloqueo por dispositivo/IP{e.ban_reason ? ` · ${e.ban_reason}` : ""}</div>
              )}
              {e.related_accounts?.length > 0 && (
                <div className="mt-2 text-[11px]">
                  <div className="text-amber-300 uppercase tracking-widest text-[9px] mb-1">Cuentas que han usado esta IP / dispositivo</div>
                  <div className="flex flex-wrap gap-1.5">
                    {e.related_accounts.map((a) => (
                      <span key={a.id} data-testid={`blocked-attempt-related-${a.id}`} className={`px-2 py-0.5 rounded-full border ${a.blocked ? "border-red-400/40 text-red-200" : "border-amber-400/40 text-amber-200"}`}>
                        {a.name || a.username} · @{a.username}{a.blocked ? " (bloqueado)" : " (posible cuenta falsa)"}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {e.user_agent && <div className="mt-1.5 text-[10px] text-neutral-600 truncate" title={e.user_agent}>{e.user_agent}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminBlockedAttemptsPanel;
